'use client';

import { useEffect, useRef, useState } from "react";
import { useCookieConsent } from '@lightwaves/hooks';
import Image from "@/components/OptimizedImage";
import { getExifData } from "@/lib/exif-loader";

interface Print {
  name: string;
  image: string;
  price?: string | number;
  currency?: string;
  availability?: string;
  description?: string;
  sku?: string;
  variations?: string[];
  weight?: number;
  itemNumber?: string;
  print?: string;
  paper?: string;
  edition?: string;
  shoplink?: string;
}

interface PrintGalleryProps {
  prints: Print[];
  siteUrl: string;
  title: string;
  ecommerceEnabled?: boolean;
  showExifData?: boolean;
}

export default function PrintGallery({ prints, siteUrl, title, ecommerceEnabled = false, showExifData = false }: PrintGalleryProps) {
  const { hasConsent } = useCookieConsent();
  const containerRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(2);
  const gap = 24; // gap in pixels

  // Determine number of columns based on screen width
  useEffect(() => {
    const updateColumns = () => {
      setColumns(window.innerWidth >= 768 ? 2 : 1);
    };
    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  // Distribute items across columns in row-first order
  const getColumnItems = () => {
    const cols: Print[][] = Array.from({ length: columns }, () => []);
    prints.forEach((print, index) => {
      cols[index % columns].push(print);
    });
    return cols;
  };

  const columnItems = getColumnItems();

  const hasValidPrice = (price: string | number | undefined): boolean => {
    if (price === undefined || price === null) return false;
    const priceStr = String(price).trim();
    if (!priceStr || priceStr.toUpperCase() === "TBD") return false;
    return !isNaN(parseFloat(priceStr));
  };


  const renderPrintItem = (print: Print, index: number) => {
    // Snipcart mode: ecommerce enabled AND user has given consent
    const snipcartMode = ecommerceEnabled && hasConsent;
    // Show Snipcart button only if in snipcart mode AND item has valid price + sku
    const showSnipcartButton = snipcartMode && hasValidPrice(print.price) && print.sku;
    // Show Buy Print link only if NOT in snipcart mode AND item has shoplink
    const showBuyPrintLink = !snipcartMode && print.shoplink;
    const variationString = print.variations ? print.variations.join('|') : "";

    return (
      <div key={index} className="flex flex-col group mb-6">
        <div className="overflow-hidden rounded-lg relative">
          <div
            className="block cursor-pointer hover:opacity-95 transition-opacity"
            role="button"
            tabIndex={0}
            aria-label={`View ${print.name}`}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
          >
            <Image
              src={print.image}
              alt={print.name || 'Print'}
              width={800}
              height={600}
              sizes="(max-width: 768px) 100vw, 50vw"
              className="rounded-lg w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
              data-lightbox-index={index}
            />
          </div>

          {showSnipcartButton && (
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 rounded-lg flex items-end justify-start p-4 pointer-events-none">
              <button
                type="button"
                className="snipcart-add-item pointer-events-auto opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 h-9 flex items-center gap-2 px-4 py-1.5 text-[10px] uppercase tracking-[0.15em] rounded-lg shadow-lg bg-transparent text-white border-2 border-white hover:bg-white hover:text-black font-bold whitespace-nowrap"
                data-item-id={print.sku}
                data-item-price={print.price}
                data-item-url="/"
                data-item-name={print.name}
                data-item-image={`${siteUrl}${print.image}`}
                data-item-currency={print.currency || 'USD'}
                data-item-shippable="true"
                data-item-weight={print.weight || 500}
                {...(variationString && { 'data-item-custom1-name': 'Size & Edition', 'data-item-custom1-options': variationString })}
                {...(print.print && { 'data-item-custom2-name': 'Print', 'data-item-custom2-value': print.print, 'data-item-custom2-type': 'readonly' })}
                {...(print.paper && { 'data-item-custom3-name': 'Paper', 'data-item-custom3-value': print.paper, 'data-item-custom3-type': 'readonly' })}
                {...(print.edition && { 'data-item-custom4-name': 'Edition', 'data-item-custom4-value': print.edition, 'data-item-custom4-type': 'readonly' })}
              >
                <span>Buy Print</span>
              </button>
            </div>
          )}
        </div>

        <div className="text-center mt-4">
          <h3 className="text-muted-foreground text-sm md:text-base leading-tight mt-0 font-medium uppercase tracking-tight">
            {print.name}
          </h3>

          {/* EXIF Data Display */}
          {(() => {
            if (!showExifData) return null;
            const exif = getExifData(print.image);
            if (!exif) return null;
            return (
              <div className="flex flex-wrap gap-3 justify-center text-xs text-gray-500 mt-2">
                {exif.camera && (
                  <span className="flex items-center gap-1" title="Camera">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    {exif.camera}
                  </span>
                )}
                {exif.lens && (
                  <span className="flex items-center gap-1" title="Lens">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth={2} /><circle cx="12" cy="12" r="6" strokeWidth={2} /><circle cx="12" cy="12" r="2" strokeWidth={2} /></svg>
                    {exif.lens}
                  </span>
                )}
                {exif.focalLength && (
                  <span className="flex items-center gap-1 font-mono" title="Focal Length">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m-3-3h6" /></svg>
                    {exif.focalLength}
                  </span>
                )}
                {exif.aperture && (
                  <span className="flex items-center gap-1 font-mono" title="Aperture">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><polygon strokeWidth={2} points="12,2 15,9 22,9 17,14 19,22 12,17 5,22 7,14 2,9 9,9" /></svg>
                    ƒ/{exif.aperture}
                  </span>
                )}
                {exif.shutterSpeed && (
                  <span className="flex items-center gap-1 font-mono" title="Shutter Speed">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth={2} /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2" /></svg>
                    {exif.shutterSpeed}s
                  </span>
                )}
                {exif.iso && (
                  <span className="flex items-center gap-1 font-mono" title="ISO">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                    ISO {exif.iso}
                  </span>
                )}
              </div>
            );
          })()}

          {print.description && (
            <p className="text-muted-foreground text-sm md:text-base leading-snug mt-1">
              {print.description}
            </p>
          )}

          {/* Show Buy Print link only when Snipcart is NOT active */}
          {showBuyPrintLink && (
            <a
              href={print.shoplink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center mt-3 px-6 py-2 text-sm font-medium rounded-lg border border-foreground/20 hover:bg-foreground hover:text-background transition-colors"
            >
              Buy Print
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className="mt-4 flex gap-6">
      {columnItems.map((column, colIndex) => (
        <div key={colIndex} className="flex-1 flex flex-col">
          {column.map((print, itemIndex) =>
            renderPrintItem(print, colIndex + itemIndex * columns)
          )}
        </div>
      ))}
    </div>
  );
}